import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useOutletContext } from 'react-router-dom';
import {
  expenditureCodesAndNames,
  formatDate,
  formatToZMW,
  handleExcel,
  handlePrint,
  revenueCodesAndNames,
  rootUrl
} from '../../../helpers';
import { useAuth } from '../../../context/auth-context';
import {
  FaBuilding,
  FaEye,
  FaEyeSlash,
  FaFileExcel,
  FaFilePdf,
  FaFilter,
  FaPrint,
  FaSpinner,
  FaTimes,
  FaToggleOff,
  FaToggleOn
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const sumOfArray = (arr) => arr.reduce((acc, num) => acc + num, 0);
const pct = (num, den) => (den > 0 ? `${((num / den) * 100).toFixed()}%` : '0%');
const isSelectedBank = (bankIds, bankId) => bankIds.some((id) => String(id) === String(bankId));
const normalizeBankIds = (bankIds) => (bankIds || []).map((id) => String(id));

function getMonthDifference(startDate, endDate) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  return (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
}

const Payments = ({ startDate, endDate, triggerFetch }) => {
  const [summaryByCoa, setSummaryByCoa] = useState({});
  const [cashBooks, setCashBooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toggle, setToggle] = useState(false);

  const [showBreakdownModal, setShowBreakdownModal] = useState(false);
  const [breakdownTitle, setBreakdownTitle] = useState('');
  const [breakdownRows, setBreakdownRows] = useState([]);
  const [breakdownTotal, setBreakdownTotal] = useState(0);
  const [breakdownLoading, setBreakdownLoading] = useState(false);

  const [columnVisibility, setColumnVisibility] = useState({
    accountCode: true,
    accountName: true,
    totalBudget: true,
    flexed: true,
    actual: true,
    actualToDate: true,
    performance: true,
    difference: true,
    budgetPerformance: true
  });

  const { allData } = useOutletContext();
  const auth = useAuth();
  const componentRef = useRef(null);

  const bankOptions = (allData?.bank || []).map((bank) => ({
    value: bank.id,
    label: bank.name
  }));

  const accountCodesAndNames = useMemo(
    () => [...revenueCodesAndNames, { id: '111', name: 'Total' }, ...expenditureCodesAndNames],
    []
  );

  useEffect(() => {
    if (!triggerFetch || !startDate || !endDate) return;
    setLoading(true);
    axios
      .post(rootUrl('/cashbookSummary'), {
        uid: auth.user?.id,
        startDate,
        endDate,
        banks: normalizeBankIds(cashBooks)
      })
      .then((response) => {
        const rows = response.data?.data?.byCoa || [];
        const map = {};
        rows.forEach((r) => {
          map[String(r.coa_code || '')] = {
            month_total: parseFloat(r.month_total) || 0,
            period_total: parseFloat(r.period_total) || 0
          };
        });
        setSummaryByCoa(map);
      })
      .catch((error) => {
        console.error('Error fetching cashbook report data', error);
        toast.error('Error fetching report data');
      })
      .finally(() => setLoading(false));
  }, [triggerFetch, startDate, endDate, auth.user?.id, cashBooks]);

  const getBankName = (bankId) =>
    allData?.bank?.find((bank) => String(bank.id) === String(bankId))?.name || bankId || '-';
  const getPayeeName = (row) => {
    const payeeId = row?.paye ?? row?.payee ?? row?.payee_id ?? row?.supplier_id ?? row?.sid;
    if (!payeeId) return row?.payeeName || '-';
    return allData?.suppliers?.find((supplier) => String(supplier.id) === String(payeeId))?.name || row?.payeeName || '-';
  };

  const year = useMemo(() => new Date(startDate).getFullYear(), [startDate]);
  const months = useMemo(() => getMonthDifference(startDate, endDate) + 1, [startDate, endDate]);

  const rowsByCode = useMemo(() => {
    const budgetRows = (allData?.budget || []).filter((b) => Number(b.year) === Number(year) && b.coa);
    const codeMap = {};

    budgetRows.forEach((b) => {
      const code = String(b.coa);
      if (!codeMap[code]) {
        codeMap[code] = {
          code,
          prefix: code.substring(0, 3),
          name: allData?.coa?.find((c) => String(c.account) === code)?.name || '-',
          budgetRows: [],
          totalBudget: 0,
          flexed: 0,
          actual: 0,
          actualToDate: 0,
          transactions: []
        };
      }
      codeMap[code].budgetRows.push(b);
      codeMap[code].totalBudget += parseFloat(b.price * b.frequency * b.qty) || 0;
    });

    Object.keys(codeMap).forEach((code) => {
      const summary = summaryByCoa[code] || { month_total: 0, period_total: 0 };
      codeMap[code].actualToDate = summary.period_total;
      codeMap[code].actual = summary.month_total;
      codeMap[code].flexed = (months / 12) * codeMap[code].totalBudget;
    });

    return Object.values(codeMap).sort((a, b) => a.code.localeCompare(b.code));
  }, [allData?.budget, allData?.coa, year, months, summaryByCoa]);

  const openBreakdownModal = async (title, coaCode) => {
    try {
      setBreakdownTitle(title);
      setBreakdownRows([]);
      setBreakdownTotal(0);
      setShowBreakdownModal(true);
      setBreakdownLoading(true);

      const response = await axios.post(rootUrl('/cashbookCodeTransactions'), {
        uid: auth.user?.id,
        startDate,
        endDate,
        mode: 'coa',
        code: coaCode,
        banks: normalizeBankIds(cashBooks)
      });
      const rows = response.data?.data?.transactions || [];

      const sortedRows = [...rows].sort((a, b) => {
        const dateDiff = new Date(a.date) - new Date(b.date);
        if (dateDiff !== 0) return dateDiff;
        return Number(a.id || 0) - Number(b.id || 0);
      });

      let runningBalance = 0;
      const prepared = sortedRows.map((row) => {
        const amount = parseFloat(row.amount) || 0;
        runningBalance += row.type === 'expense' ? -amount : amount;
        return { ...row, amount, runningBalance };
      });

      setBreakdownRows(prepared);
      setBreakdownTotal(sumOfArray(prepared.map((r) => r.amount)));
    } catch (error) {
      console.error('Error fetching code transactions', error);
      toast.error('Error fetching transaction details');
      setBreakdownRows([]);
      setBreakdownTotal(0);
    } finally {
      setBreakdownLoading(false);
    }
  };

  const renderTableRows = () => {
    const rows = [];
    let totalBudgetGroup = 0;
    let totalFlexedGroup = 0;
    let totalActualGroup = 0;
    let totalActualToDateGroup = 0;

    accountCodesAndNames.forEach((account) => {
      const accountRows = rowsByCode.filter((entry) => entry.prefix === account.id);
      if (accountRows.length === 0 && account.id !== '111' && account.id !== '000') return;

      let subtotalBudget = 0;
      let subtotalFlexed = 0;
      let subtotalActual = 0;
      let subtotalActualToDate = 0;

      accountRows.forEach((entry) => {
        subtotalBudget += entry.totalBudget;
        subtotalFlexed += entry.flexed;
        subtotalActual += entry.actual;
        subtotalActualToDate += entry.actualToDate;

        if (!toggle) {
          rows.push(
            <tr key={entry.code} className="hover:bg-gray-50 transition-colors border-b border-gray-200">
              {columnVisibility.accountCode && (
                <td className="border border-gray-200 py-2.5 px-4 text-sm text-gray-700">
                  <button
                    type="button"
                    onClick={() => openBreakdownModal(`COA ${entry.code} - ${entry.name}`, entry.code)}
                    className="text-blue-700 hover:text-blue-900 hover:underline font-semibold"
                  >
                    {entry.code}
                  </button>
                </td>
              )}
              {columnVisibility.accountName && (
                <td className="border border-gray-200 py-2.5 px-4 text-sm text-gray-800 font-medium">{entry.name}</td>
              )}
              {columnVisibility.totalBudget && <td className="border border-gray-200 py-2.5 px-4 text-sm text-right">{formatToZMW(entry.totalBudget)}</td>}
              {columnVisibility.flexed && <td className="border border-gray-200 py-2.5 px-4 text-sm text-right">{formatToZMW(entry.flexed)}</td>}
              {columnVisibility.actual && <td className="border border-gray-200 py-2.5 px-4 text-sm text-right">{formatToZMW(entry.actual)}</td>}
              {columnVisibility.actualToDate && <td className="border border-gray-200 py-2.5 px-4 text-sm text-right font-medium">{formatToZMW(entry.actualToDate)}</td>}
              {columnVisibility.performance && <td className="border border-gray-200 py-2.5 px-4 text-sm text-right">{pct(entry.actualToDate, entry.flexed)}</td>}
              {columnVisibility.difference && <td className="border border-gray-200 py-2.5 px-4 text-sm text-right">{formatToZMW(entry.totalBudget - entry.actualToDate)}</td>}
              {columnVisibility.budgetPerformance && <td className="border border-gray-200 py-2.5 px-4 text-sm text-right">{pct(entry.actualToDate, entry.totalBudget)}</td>}
            </tr>
          );
        }
      });

      if (accountRows.length > 0) {
        rows.push(
          <tr key={`${account.id}-total`} className={`${!toggle ? 'bg-gray-800 text-white' : 'bg-gray-100'} border-t-2 border-gray-400`}>
            {columnVisibility.accountCode && <td className="border border-gray-300 py-3 px-4 font-semibold text-sm">{account.id}</td>}
            {columnVisibility.accountName && <td className="border border-gray-300 py-3 px-4 font-semibold text-sm">{account.name}</td>}
            {columnVisibility.totalBudget && <td className="border border-gray-300 py-3 px-4 text-right font-semibold text-sm">{formatToZMW(subtotalBudget)}</td>}
            {columnVisibility.flexed && <td className="border border-gray-300 py-3 px-4 text-right font-semibold text-sm">{formatToZMW(subtotalFlexed)}</td>}
            {columnVisibility.actual && <td className="border border-gray-300 py-3 px-4 text-right font-semibold text-sm">{formatToZMW(subtotalActual)}</td>}
            {columnVisibility.actualToDate && <td className="border border-gray-300 py-3 px-4 text-right font-semibold text-sm">{formatToZMW(subtotalActualToDate)}</td>}
            {columnVisibility.performance && <td className="border border-gray-300 py-3 px-4 text-right font-semibold text-sm">{pct(subtotalActualToDate, subtotalFlexed)}</td>}
            {columnVisibility.difference && <td className="border border-gray-300 py-3 px-4 text-right font-semibold text-sm">{formatToZMW(subtotalBudget - subtotalActualToDate)}</td>}
            {columnVisibility.budgetPerformance && <td className="border border-gray-300 py-3 px-4 text-right font-semibold text-sm">{pct(subtotalActualToDate, subtotalBudget)}</td>}
          </tr>
        );
      }

      totalBudgetGroup += subtotalBudget;
      totalFlexedGroup += subtotalFlexed;
      totalActualGroup += subtotalActual;
      totalActualToDateGroup += subtotalActualToDate;

      if (account.id === '111') {
        rows.push(
          <tr key="total-income" className="bg-gradient-to-r from-green-600 to-green-700 text-white border-t-4 border-green-800">
            <td colSpan={2} className="px-4 py-3 border border-green-500 font-bold text-base">Total Income</td>
            {columnVisibility.totalBudget && <td className="border border-green-500 py-3 px-4 text-right font-bold text-base">{formatToZMW(totalBudgetGroup)}</td>}
            {columnVisibility.flexed && <td className="border border-green-500 py-3 px-4 text-right font-bold text-base">{formatToZMW(totalFlexedGroup)}</td>}
            {columnVisibility.actual && <td className="border border-green-500 py-3 px-4 text-right font-bold text-base">{formatToZMW(totalActualGroup)}</td>}
            {columnVisibility.actualToDate && <td className="border border-green-500 py-3 px-4 text-right font-bold text-base">{formatToZMW(totalActualToDateGroup)}</td>}
            {columnVisibility.performance && <td className="border border-green-500 py-3 px-4 text-right font-bold text-base">{pct(totalActualToDateGroup, totalFlexedGroup)}</td>}
            {columnVisibility.difference && <td className="border border-green-500 py-3 px-4 text-right font-bold text-base">{formatToZMW(totalBudgetGroup - totalActualToDateGroup)}</td>}
            {columnVisibility.budgetPerformance && <td className="border border-green-500 py-3 px-4 text-right font-bold text-base">{pct(totalActualToDateGroup, totalBudgetGroup)}</td>}
          </tr>
        );
        totalBudgetGroup = 0;
        totalFlexedGroup = 0;
        totalActualGroup = 0;
        totalActualToDateGroup = 0;
      }

      if (account.id === '000') {
        rows.push(
          <tr key="total-expenditure" className="bg-gradient-to-r from-red-600 to-red-700 text-white border-t-4 border-red-800">
            <td colSpan={2} className="px-4 py-3 border border-red-500 font-bold text-base">Total Expenditure</td>
            {columnVisibility.totalBudget && <td className="border border-red-500 py-3 px-4 text-right font-bold text-base">{formatToZMW(totalBudgetGroup)}</td>}
            {columnVisibility.flexed && <td className="border border-red-500 py-3 px-4 text-right font-bold text-base">{formatToZMW(totalFlexedGroup)}</td>}
            {columnVisibility.actual && <td className="border border-red-500 py-3 px-4 text-right font-bold text-base">{formatToZMW(totalActualGroup)}</td>}
            {columnVisibility.actualToDate && <td className="border border-red-500 py-3 px-4 text-right font-bold text-base">{formatToZMW(totalActualToDateGroup)}</td>}
            {columnVisibility.performance && <td className="border border-red-500 py-3 px-4 text-right font-bold text-base">{pct(totalActualToDateGroup, totalFlexedGroup)}</td>}
            {columnVisibility.difference && <td className="border border-red-500 py-3 px-4 text-right font-bold text-base">{formatToZMW(totalBudgetGroup - totalActualToDateGroup)}</td>}
            {columnVisibility.budgetPerformance && <td className="border border-red-500 py-3 px-4 text-right font-bold text-base">{pct(totalActualToDateGroup, totalBudgetGroup)}</td>}
          </tr>
        );
      }
    });

    return rows;
  };

  const handleColumnToggle = (column) => {
    setColumnVisibility((prev) => ({ ...prev, [column]: !prev[column] }));
  };

  const handleExportToPDF = () => {
    const input = componentRef.current;
    if (!input) return;

    html2canvas(input, { scrollY: -window.scrollY }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
      heightLeft -= pdfHeight;
      while (heightLeft > 0) {
        position -= pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
        heightLeft -= pdfHeight;
      }
      pdf.save(`Payments_${startDate}_${endDate}.pdf`);
    });
  };

  const handleExportToExcel = () => handleExcel(componentRef, `Payments_${startDate}_${endDate}`);

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-64 bg-gray-50 rounded-xl border border-gray-200">
        <FaSpinner size={50} className="animate-spin text-blue-950 mb-4" />
        <p className="text-gray-600">Loading report data...</p>
      </div>
    );
  }

  return (
    <div className="cash-report space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-blue-950 rounded-lg">
          <FaBuilding className="text-white text-xl" />
        </div>
        <div>
          <h1 className="font-bold text-xl md:text-2xl text-gray-900">Receipts and Payments By COA</h1>
          <p className="text-sm text-gray-500">Budget vs actual by chart of accounts</p>
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-2">
            <FaFilter className="text-gray-600" />
            <label className="text-sm font-medium text-gray-700">Filter by Bank:</label>
          </div>
          <div className="flex-1 max-w-md">
            <Select
              options={bankOptions}
              placeholder="Select banks..."
              isClearable
              isMulti
              className="text-sm"
              value={bankOptions.filter((option) => isSelectedBank(cashBooks, option.value))}
              onChange={(selectedOptions) => setCashBooks(selectedOptions ? selectedOptions.map((opt) => opt.value) : [])}
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-gray-300">
          <div className="flex items-center gap-2 flex-wrap text-sm text-gray-600">
            {toggle ? <FaEyeSlash /> : <FaEye />}
            <button className="flex items-center gap-2 px-4 py-2 bg-blue-950 text-white rounded-lg hover:bg-blue-900 transition-colors font-medium text-sm" onClick={() => setToggle(!toggle)}>
              {toggle ? <FaToggleOn size={20} /> : <FaToggleOff size={20} />}
              <span>{toggle ? 'Show Details' : 'Hide Details'}</span>
            </button>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-700">Columns:</span>
            <div className="flex flex-wrap gap-2">
              {Object.keys(columnVisibility).map((column) => (
                <label key={column} className="flex items-center space-x-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm hover:bg-gray-50 cursor-pointer transition-colors">
                  <input type="checkbox" checked={columnVisibility[column]} onChange={() => handleColumnToggle(column)} className="h-4 w-4 text-blue-600" />
                  <span className="text-xs font-medium text-gray-700 capitalize">{column.replace(/([A-Z])/g, ' $1').trim()}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        <button onClick={handleExportToExcel} className="px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm flex items-center gap-2 shadow-sm"><FaFileExcel /><span>Excel</span></button>
        <button onClick={handleExportToPDF} className="px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm flex items-center gap-2 shadow-sm"><FaFilePdf /><span>PDF</span></button>
        <button onClick={() => handlePrint(componentRef)} className="px-4 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-800 text-sm flex items-center gap-2 shadow-sm"><FaPrint /><span>Print</span></button>
      </div>

      <div ref={componentRef} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="text-center py-6 px-4">
          <h2 className="font-bold text-xl md:text-2xl mb-2">{allData.council[0]?.name.toUpperCase()}</h2>
          <h2 className="font-bold text-lg md:text-xl mb-3">{'Receipts and Payments By COA'.toUpperCase()}</h2>
          <h3 className="text-base md:text-lg font-medium opacity-90">From {formatDate(startDate)} to {formatDate(endDate)}</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gradient-to-r from-blue-950 to-blue-900 text-white">
                {columnVisibility.accountCode && <th className="border border-gray-300 py-3 px-4 text-left font-semibold text-sm">Account Code</th>}
                {columnVisibility.accountName && <th className="border border-gray-300 py-3 px-4 text-left font-semibold text-sm">Account Name</th>}
                {columnVisibility.totalBudget && <th className="border border-gray-300 py-3 px-4 text-right font-semibold text-sm">Total Budget</th>}
                {columnVisibility.flexed && <th className="border border-gray-300 py-3 px-4 text-right font-semibold text-sm">Flexed</th>}
                {columnVisibility.actual && <th className="border border-gray-300 py-3 px-4 text-right font-semibold text-sm">Actual</th>}
                {columnVisibility.actualToDate && <th className="border border-gray-300 py-3 px-4 text-right font-semibold text-sm">Actual to Date</th>}
                {columnVisibility.performance && <th className="border border-gray-300 py-3 px-4 text-right font-semibold text-sm">Performance</th>}
                {columnVisibility.difference && <th className="border border-gray-300 py-3 px-4 text-right font-semibold text-sm">Difference</th>}
                {columnVisibility.budgetPerformance && <th className="border border-gray-300 py-3 px-4 text-right font-semibold text-sm">Budget Performance</th>}
              </tr>
            </thead>
            <tbody className="bg-white">{renderTableRows()}</tbody>
          </table>
        </div>
      </div>

      {showBreakdownModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden border border-gray-200">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-base md:text-lg font-semibold text-gray-900">{breakdownTitle}</h3>
              <button type="button" onClick={() => setShowBreakdownModal(false)} className="p-2 rounded-lg hover:bg-gray-200 text-gray-600"><FaTimes /></button>
            </div>

            <div className="p-5 overflow-auto max-h-[70vh]">
              {breakdownLoading ? (
                <div className="text-center py-10 text-gray-600">Loading transactions...</div>
              ) : breakdownRows.length === 0 ? (
                <div className="text-center py-10 text-gray-600">No transactions found for this code.</div>
              ) : (
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-blue-950 text-white">
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm">Date</th>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm">Voucher/Ref</th>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm">Type</th>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm">Description</th>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm">Bank</th>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm">Payee</th>
                      <th className="border border-gray-300 px-3 py-2 text-right text-sm">Amount</th>
                      <th className="border border-gray-300 px-3 py-2 text-right text-sm">Running Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {breakdownRows.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50">
                        <td className="border border-gray-200 px-3 py-2 text-sm">{formatDate(row.date)}</td>
                        <td className="border border-gray-200 px-3 py-2 text-sm">{row.voucher || row.cheque || '-'}</td>
                        <td className="border border-gray-200 px-3 py-2 text-sm capitalize">{row.type || '-'}</td>
                        <td className="border border-gray-200 px-3 py-2 text-sm">{row.description || '-'}</td>
                        <td className="border border-gray-200 px-3 py-2 text-sm">{getBankName(row.bank)}</td>
                        <td className="border border-gray-200 px-3 py-2 text-sm">{getPayeeName(row)}</td>
                        <td className="border border-gray-200 px-3 py-2 text-sm text-right">{formatToZMW(row.amount)}</td>
                        <td className="border border-gray-200 px-3 py-2 text-sm text-right">{formatToZMW(row.runningBalance)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-100 font-semibold">
                      <td colSpan={6} className="border border-gray-300 px-3 py-2 text-right">Totals</td>
                      <td className="border border-gray-300 px-3 py-2 text-right">{formatToZMW(breakdownTotal)}</td>
                      <td className="border border-gray-300 px-3 py-2 text-right">{formatToZMW(breakdownRows[breakdownRows.length - 1]?.runningBalance || 0)}</td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;
